import { useEffect, useRef, useState } from "react";
import MuxPlayer, { type MuxPlayerRefAttributes } from "@mux/mux-player-react";
import { apiFetch } from "./lib/auth";

type Playback = {
  playbackId: string;
  tokens: { playback: string; thumbnail: string; storyboard: string } | null;
};

/**
 * Plays a Mux-hosted lesson.
 *
 * The playback id alone is not enough for a private asset, so the component
 * asks the API for a signed token first; the API decides access there, using
 * the same policy that guards every other lesson resource. `tokens: null` means
 * the asset was published with a public policy (free previews) and plays as-is.
 */
export function MuxLessonPlayer({
  lessonId,
  title,
  startAt,
  onReady,
  onPosition,
  onEnded,
  fullscreenElement,
}: {
  lessonId: string;
  title: string;
  /**
   * Id of an ancestor to take fullscreen instead of the player itself, so
   * overlays drawn over the video (recommendation cards) stay visible.
   */
  fullscreenElement?: string;
  startAt: number;
  onReady: () => void;
  onPosition: (seconds: number) => void;
  onEnded: () => void;
}) {
  const [playback, setPlayback] = useState<Playback | null>(null);
  const [error, setError] = useState(false);
  const playerRef = useRef<MuxPlayerRefAttributes>(null);

  useEffect(() => {
    let cancelled = false;
    setPlayback(null);
    setError(false);
    apiFetch<Playback>(`/api/education/lessons/${lessonId}/playback`)
      .then((result) => {
        if (!cancelled) setPlayback(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  // Space plays/pauses the lesson without scrolling the page. Inside the
  // player media-chrome already does this; elsewhere the browser would scroll,
  // so handle it here unless the key belongs to a field or a control.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const player = playerRef.current;
      if (event.key !== " " || !player || event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && player.contains(target)) return;
      if (
        target?.closest(
          "input, textarea, select, button, a, summary, [contenteditable=''], [contenteditable='true'], [role='button'], [role='textbox'], [role='menuitem'], [role='dialog']",
        )
      )
        return;
      event.preventDefault();
      if (event.repeat) return;
      if (player.paused) void player.play();
      else player.pause();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (error)
    return (
      <div className="text-paper absolute inset-0 grid place-items-center p-6 text-center text-sm">
        No pudimos preparar este video. Recarga la página o vuelve a intentarlo.
      </div>
    );
  if (!playback) return <div className="video-glow absolute inset-0" />;

  return (
    <MuxPlayer
      ref={playerRef}
      playbackId={playback.playbackId}
      tokens={
        playback.tokens
          ? {
              playback: playback.tokens.playback,
              thumbnail: playback.tokens.thumbnail,
              storyboard: playback.tokens.storyboard,
            }
          : undefined
      }
      metadata={{ video_id: lessonId, video_title: title }}
      startTime={startAt > 0 ? startAt : undefined}
      // "any" tries to start with sound and falls back to muted only when the
      // browser blocks it. Opening a lesson is a click, which browsers count as
      // the user activation that allows audible autoplay.
      autoPlay="any"
      streamType="on-demand"
      onLoadedMetadata={(event) => {
        // The fullscreen-element attribute is only honoured when it changes
        // after the player has set up its controller, which React's first
        // render never does, so point the controller at the ancestor here.
        const controller = (
          event.currentTarget as unknown as {
            mediaController?: { fullscreenElement: HTMLElement };
          }
        ).mediaController;
        const target = fullscreenElement
          ? document.getElementById(fullscreenElement)
          : null;
        if (controller && target) controller.fullscreenElement = target;
        onReady();
      }}
      onEnded={onEnded}
      onTimeUpdate={(event) =>
        onPosition(
          Math.floor((event.currentTarget as HTMLMediaElement).currentTime),
        )
      }
      className="absolute inset-0 h-full w-full"
      style={{ aspectRatio: "16/9" }}
    />
  );
}
